<template>
  <div class="legal-page">
    <!-- Navbar -->
    <MainNavbar />

    <!-- Hero Banner -->
    <div class="legal-hero community-guidelines-hero">
      <div class="hero-content">
        <div class="guidelines-icon">
          <span class="icon-emoji">📘</span>
          <div class="icon-badge">
            <i class="fa-solid fa-users"></i>
          </div>
        </div>
        <h1>IPA Community Guidelines</h1>
        <p>Keeping our marketplace safe, trustworthy, and enjoyable for everyone</p>
        <div class="last-updated">
          <i class="fa-solid fa-calendar"></i>
          Last Updated: April 27, 2026
        </div>
        <div class="applicability-badge">
          <i class="fa-solid fa-user-group"></i>
          Applies to: All Buyers & Sellers
        </div>
      </div>
      <div class="hero-actions">
        <router-link to="/ipromiseafrica/resale/buyer-product-view" class="hero-btn primary">
          <i class="fa-solid fa-store"></i>
          Visit Marketplace
        </router-link>
        <router-link to="/ipromiseafrica/legal/seller-terms" class="hero-btn secondary">
          <i class="fa-solid fa-file-signature"></i>
          Seller Terms
        </router-link>
      </div>
      <LegalDocumentSeal />
    </div>

    <!-- Welcome Message -->
    <div class="welcome-message">
      <div class="welcome-content">
        <h2>Welcome to the IPromiseAfrica (IPA) community!</h2>
        <p>
          To keep our marketplace safe, trustworthy, and enjoyable for everyone, all users — 
          buyers and sellers — must follow the guidelines below.
        </p>
        <div class="guidelines-scope">
          <i class="fa-solid fa-scroll"></i>
          <span>These guidelines apply to all activity on the platform, including listings, reviews  and interactions.</span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="legal-container">
      <div class="community-guidelines-content">
        <!-- Guidelines Grid -->
        <div class="guidelines-grid">
          <!-- Respect & Fairness -->
          <div class="guideline-card" data-aos="fade-up">
            <div class="guideline-number">1</div>
            <div class="guideline-header">
              <div class="guideline-title">
                <i class="fa-solid fa-handshake"></i>
                <h3>Respect & Fairness</h3>
              </div>
              <p class="guideline-subtitle">Treat everyone with dignity and professionalism</p>
            </div>
            <div class="guideline-body">
              <div class="allowed-section">
                <div class="section-header allowed">
                  <i class="fa-solid fa-circle-check"></i>
                  <h4>What's Allowed</h4>
                </div>
                <div class="allowed-list">
                  <div class="allowed-item">
                    <i class="fa-solid fa-user-check"></i>
                    <span>Treat all users with courtesy, honesty, and respect</span>
                  </div>
                  <div class="allowed-item">
                    <i class="fa-solid fa-comment-dots"></i>
                    <span>Communicate clearly and professionally</span>
                  </div>
                  <div class="allowed-item">
                    <i class="fa-solid fa-peace"></i>
                    <span>Resolve disputes calmly and reasonably</span>
                  </div>
                </div>
              </div>
              <div class="prohibited-section">
                <div class="section-header prohibited">
                  <i class="fa-solid fa-circle-xmark"></i>
                  <h4>Not Allowed</h4>
                </div>
                <div class="prohibited-list">
                  <div class="prohibited-item">
                    <i class="fa-solid fa-user-slash"></i>
                    <span>Harassment, threats, abuse, insults, or hate speech</span>
                  </div>
                  <div class="prohibited-item">
                    <i class="fa-solid fa-ban"></i>
                    <span>Discriminatory behavior based on race, gender, religion, ethnicity, disability, or any other protected attribute</span>
                  </div>
                  <div class="prohibited-item">
                    <i class="fa-solid fa-fire"></i>
                    <span>Intentionally provoking or attacking other users</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Safe Buying & Selling -->
          <div class="guideline-card" data-aos="fade-up" data-aos-delay="100">
            <div class="guideline-number">2</div>
            <div class="guideline-header">
              <div class="guideline-title">
                <i class="fa-solid fa-shield"></i>
                <h3>Safe Buying & Selling</h3>
              </div>
              <p class="guideline-subtitle">Secure transactions for everyone</p>
            </div>
            <div class="guideline-body">
              <div class="dual-column">
                <!-- Buyers Column -->
                <div class="role-section buyer-section">
                  <div class="role-header">
                    <i class="fa-solid fa-cart-shopping"></i>
                    <h4>Buyer Responsibilities</h4>
                  </div>
                  <div class="role-rules">
                    <div class="rule">
                      <i class="fa-solid fa-question-circle"></i>
                      <span>Ask questions before purchasing if something is unclear</span>
                    </div>
                    <div class="rule">
                      <i class="fa-solid fa-location-dot"></i>
                      <span>Meet sellers in safe, public areas for hand-to-hand exchanges</span>
                    </div>
                    <div class="rule">
                      <i class="fa-solid fa-flag"></i>
                      <span>Report suspicious listings or behavior immediately</span>
                    </div>
                  </div>
                </div>
                
                <!-- Sellers Column -->
                <div class="role-section seller-section">
                  <div class="role-header">
                    <i class="fa-solid fa-store"></i>
                    <h4>Seller Responsibilities</h4>
                  </div>
                  <div class="role-rules">
                    <div class="rule">
                      <i class="fa-solid fa-clipboard-check"></i>
                      <span>Provide accurate descriptions and images</span>
                    </div>
                    <div class="rule">
                      <i class="fa-solid fa-star"></i>
                      <span>State the real condition of items</span>
                    </div>
                    <div class="rule">
                      <i class="fa-solid fa-reply"></i>
                      <span>Respond to inquiries promptly and honestly</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="prohibited-section">
                <div class="section-header prohibited">
                  <i class="fa-solid fa-circle-xmark"></i>
                  <h4>Not Allowed for Anyone</h4>
                </div>
                <div class="prohibited-list">
                  <div class="prohibited-item">
                    <i class="fa-solid fa-skull-crossbones"></i>
                    <span>Selling illegal, counterfeit, banned, or unsafe goods</span>
                  </div>
                  <div class="prohibited-item">
                    <i class="fa-solid fa-mask"></i>
                    <span>Misleading descriptions or hidden defects</span>
                  </div>
                  <div class="prohibited-item">
                    <i class="fa-solid fa-fish"></i>
                    <span>Setting traps, scams, or fraudulent meet-ups</span>
                  </div>
                  <div class="prohibited-item">
                    <i class="fa-solid fa-door-closed"></i>
                    <span>Attempting to transact outside the platform to avoid safeguards</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Prohibited Items & Activities -->
          <div class="guideline-card" data-aos="fade-up" data-aos-delay="200">
            <div class="guideline-number">3</div>
            <div class="guideline-header">
              <div class="guideline-title">
                <i class="fa-solid fa-ban"></i>
                <h3>Prohibited Items & Activities</h3>
              </div>
              <p class="guideline-subtitle">What you cannot list, buy, or request</p>
            </div>
            <div class="guideline-body">
              <div class="prohibited-items-grid">
                <div class="prohibited-item-category">
                  <div class="category-header">
                    <i class="fa-solid fa-gavel"></i>
                    <h5>Illegal Items</h5>
                  </div>
                  <div class="category-items">
                    <span>Stolen goods</span>
                    <span>Weapons, firearms, or explosives</span>
                    <span>Illegal drugs or controlled substances</span>
                  </div>
                </div>
                <div class="prohibited-item-category">
                  <div class="category-header">
                    <i class="fa-solid fa-copyright"></i>
                    <h5>Intellectual Property</h5>
                  </div>
                  <div class="category-items">
                    <span>Fake or counterfeit branded products</span>
                    <span>Items that violate copyright</span>
                  </div>
                </div>
                <div class="prohibited-item-category">
                  <div class="category-header">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <h5>Dangerous Items</h5>
                  </div>
                  <div class="category-items">
                    <span>Hazardous or recalled items</span>
                    <span>Items obtained through fraud</span>
                  </div>
                </div>
                <div class="prohibited-item-category">
                  <div class="category-header">
                    <i class="fa-solid fa-eye-slash"></i>
                    <h5>Restricted Content</h5>
                  </div>
                  <div class="category-items">
                    <span>Adult or sexually explicit products</span>
                    <span>Services related to illegal activity</span>
                  </div>
                </div>
              </div>
              <div class="guideline-warning critical">
                <i class="fa-solid fa-circle-exclamation"></i>
                <p>Listing prohibited items will result in immediate account suspension and potential legal action.</p>
              </div>
            </div>
          </div>

          <!-- Privacy & Personal Information -->
          <div class="guideline-card" data-aos="fade-up" data-aos-delay="300">
            <div class="guideline-number">4</div>
            <div class="guideline-header">
              <div class="guideline-title">
                <i class="fa-solid fa-lock"></i>
                <h3>Privacy & Personal Information</h3>
              </div>
              <p class="guideline-subtitle">Protecting everyone's data</p>
            </div>
            <div class="guideline-body">
              <div class="dual-column">
                <div class="allowed-section">
                  <div class="section-header allowed">
                    <i class="fa-solid fa-circle-check"></i>
                    <h4>Allowed</h4>
                  </div>
                  <div class="allowed-list">
                    <div class="allowed-item">
                      <i class="fa-solid fa-phone"></i>
                      <span>Only exchange necessary contact information for completing an order</span>
                    </div>
                    <div class="allowed-item">
                      <i class="fa-solid fa-key"></i>
                      <span>Keep your login, OTPs, addresses, and payment details secure</span>
                    </div>
                  </div>
                </div>
                <div class="prohibited-section">
                  <div class="section-header prohibited">
                    <i class="fa-solid fa-circle-xmark"></i>
                    <h4>Not Allowed</h4>
                  </div>
                  <div class="prohibited-list">
                    <div class="prohibited-item">
                      <i class="fa-solid fa-user-secret"></i>
                      <span>Sharing someone else's personal information without consent</span>
                    </div>
                    <div class="prohibited-item">
                      <i class="fa-solid fa-database"></i>
                      <span>Mining user data or scraping the platform</span>
                    </div>
                    <div class="prohibited-item">
                      <i class="fa-solid fa-user-ninja"></i>
                      <span>Impersonation—using a fake identity or pretending to be IPA staff</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Fraud Prevention -->
          <div class="guideline-card" data-aos="fade-up" data-aos-delay="400">
            <div class="guideline-number">5</div>
            <div class="guideline-header">
              <div class="guideline-title">
                <i class="fa-solid fa-user-shield"></i>
                <h3>Fraud Prevention</h3>
              </div>
              <p class="guideline-subtitle">Keeping the marketplace honest</p>
            </div>
            <div class="guideline-body">
              <div class="fraud-grid">
                <div class="fraud-item">
                  <i class="fa-solid fa-user-xmark"></i>
                  <span>Fake accounts</span>
                </div>
                <div class="fraud-item">
                  <i class="fa-solid fa-comment-slash"></i>
                  <span>False claims about products</span>
                </div>
                <div class="fraud-item">
                  <i class="fa-solid fa-credit-card"></i>
                  <span>Chargeback abuse</span>
                </div>
                <div class="fraud-item">
                  <i class="fa-solid fa-money-bill-wave"></i>
                  <span>Payment fraud</span>
                </div>
                <div class="fraud-item">
                  <i class="fa-solid fa-chart-line"></i>
                  <span>Money laundering schemes</span>
                </div>
                <div class="fraud-item">
                  <i class="fa-solid fa-envelope-circle-check"></i>
                  <span>Spamming or unsolicited promotions</span>
                </div>
                <div class="fraud-item">
                  <i class="fa-solid fa-star-half-stroke"></i>
                  <span>Posting misleading reviews</span>
                </div>
              </div>
              <div class="guideline-warning">
                <i class="fa-solid fa-gavel"></i>
                <p>Violations may lead to suspension, removal of listings, or permanent account bans.</p>
              </div>
            </div>
          </div>

          <!-- Safety During Meet-ups -->
          <div class="guideline-card" data-aos="fade-up" data-aos-delay="500">
            <div class="guideline-number">6</div>
            <div class="guideline-header">
              <div class="guideline-title">
                <i class="fa-solid fa-people-arrows"></i>
                <h3>Safety During Meet-ups</h3>
              </div>
              <p class="guideline-subtitle">In-person exchange guidelines</p>
            </div>
            <div class="guideline-body">
              <div class="safety-tips">
                <div class="safety-tip">
                  <div class="tip-icon">
                    <i class="fa-solid fa-sun"></i>
                  </div>
                  <div class="tip-content">
                    <h5>Meet in open, well-lit public areas</h5>
                    <p>Choose locations like shopping malls, cafes, or police station parking lots</p>
                  </div>
                </div>
                <div class="safety-tip">
                  <div class="tip-icon">
                    <i class="fa-solid fa-user-group"></i>
                  </div>
                  <div class="tip-content">
                    <h5>Bring a friend if possible</h5>
                    <p>Having company adds an extra layer of security</p>
                  </div>
                </div>
                <div class="safety-tip">
                  <div class="tip-icon">
                    <i class="fa-solid fa-magnifying-glass"></i>
                  </div>
                  <div class="tip-content">
                    <h5>Inspect items carefully before paying</h5>
                    <p>Verify condition, authenticity, and functionality on the spot</p>
                  </div>
                </div>
              </div>
              <div class="disclaimer-notice">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <p>
                  <strong>Important:</strong> IPA is not responsible for unsafe meetups, but we strongly encourage caution 
                  and following these safety guidelines.
                </p>
              </div>
            </div>
          </div>

          <!-- User-Generated Content Rules -->
          <div class="guideline-card" data-aos="fade-up" data-aos-delay="600">
            <div class="guideline-number">7</div>
            <div class="guideline-header">
              <div class="guideline-title">
                <i class="fa-solid fa-photo-film"></i>
                <h3>User-Generated Content Rules</h3>
              </div>
              <p class="guideline-subtitle">What you can and cannot post</p>
            </div>
            <div class="guideline-body">
              <div class="content-rules">
                <div class="content-rule allowed">
                  <i class="fa-solid fa-check"></i>
                  <span>Must be truthful and accurate</span>
                </div>
                <div class="content-rule prohibited">
                  <i class="fa-solid fa-xmark"></i>
                  <span>Must not contain nudity or explicit material</span>
                </div>
                <div class="content-rule prohibited">
                  <i class="fa-solid fa-xmark"></i>
                  <span>Must not contain violence, graphic images, or hateful content</span>
                </div>
              </div>
              <div class="guideline-note">
                <i class="fa-solid fa-trash"></i>
                <p>IPA may remove any listing or content that violates these rules.</p>
              </div>
            </div>
          </div>

          <!-- Consequences for Violations -->
          <div class="guideline-card" data-aos="fade-up" data-aos-delay="700">
            <div class="guideline-number">8</div>
            <div class="guideline-header">
              <div class="guideline-title">
                <i class="fa-solid fa-gavel"></i>
                <h3>Consequences for Violations</h3>
              </div>
              <p class="guideline-subtitle">What happens when rules are broken</p>
            </div>
            <div class="guideline-body">
              <div class="consequences-grid">
                <div class="consequence-level">
                  <div class="level-header minor">
                    <i class="fa-solid fa-exclamation-circle"></i>
                    <h5>Minor Violations</h5>
                  </div>
                  <div class="level-actions">
                    <span>Remove listings</span>
                    <span>Restrict platform access</span>
                  </div>
                </div>
                <div class="consequence-level">
                  <div class="level-header moderate">
                    <i class="fa-solid fa-ban"></i>
                    <h5>Moderate Violations</h5>
                  </div>
                  <div class="level-actions">
                    <span>Suspend accounts</span>
                    <span>Temporary platform bans</span>
                  </div>
                </div>
                <div class="consequence-level">
                  <div class="level-header severe">
                    <i class="fa-solid fa-user-slash"></i>
                    <h5>Severe Violations</h5>
                  </div>
                  <div class="level-actions">
                    <span>Permanent account bans</span>
                    <span>Report to authorities</span>
                  </div>
                </div>
              </div>
              <div class="guideline-warning critical">
                <i class="fa-solid fa-skull-crossbones"></i>
                <p>
                  Serious violations (fraud, violence, illegal goods) result in instant termination 
                  and potential legal consequences.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Community Pledge -->
        <div class="community-pledge">
          <div class="pledge-header">
            <i class="fa-solid fa-handshake-simple"></i>
            <h3>IPA Community Pledge</h3>
          </div>
          <div class="pledge-content">
            <p>
              By using the IPromiseAfrica Marketplace, I pledge to uphold these community guidelines, 
              treat fellow users with respect, and contribute to making IPA a safe and trustworthy 
              platform for everyone.
            </p>
            <div class="pledge-brand">
              <img
                class="pledge-logo"
                src="../../assets/ipacomm.png"
                alt="IPA Community"
                loading="lazy"
                decoding="async"
              />
              <span class="pledge-brand-text">The IPA Community</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import LegalDocumentSeal from "../../components/legal/LegalDocumentSeal.vue";
import AOS from 'aos';
import 'aos/dist/aos.css';

export default {
  name: "CommunityGuidelines",
  components: {
    MainNavbar,
    MainFooter,
    LegalDocumentSeal,
  },
  mounted() {
    // Initialize animations
    AOS.init({
      duration: 600,
      once: true,
      offset: 100
    });
    
    // Scroll to top
    window.scrollTo(0, 0);
  }
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  font-family: "Poppins", sans-serif;
  background: var(--background-color1);
  color: var(--text-color1);
  overflow-x: clip;
}

.legal-hero.community-guidelines-hero {
  background: linear-gradient(
    145deg,
    var(--background-color2) 0%,
    color-mix(in srgb, var(--background-color1) 88%, var(--background-color2)) 100%
  );
  color: var(--text-color1);
  padding: clamp(3rem, 8vw, 5rem) clamp(1rem, 4vw, 1.5rem) clamp(2rem, 5vw, 2.5rem);
  text-align: center;
  margin-top: max(70px, calc(env(safe-area-inset-top, 0px) + 64px));
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.legal-hero.community-guidelines-hero .hero-content,
.legal-hero.community-guidelines-hero .hero-actions {
  position: relative;
  z-index: 2;
}

.legal-hero.community-guidelines-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 80%, color-mix(in srgb, var(--border-color-hover) 12%, transparent) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, color-mix(in srgb, var(--border-color-hover) 6%, transparent) 0%, transparent 50%);
  pointer-events: none;
}

.guidelines-icon {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
}

.icon-emoji {
  font-size: clamp(3.5rem, 10vw, 5rem);
  display: block;
  line-height: 1;
}

.icon-badge {
  position: absolute;
  bottom: -5px;
  right: -5px;
  background: var(--border-color-hover);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid var(--background-color2);
}

.icon-badge i {
  color: var(--text-color3);
  font-size: 1.2rem;
}

.hero-content h1 {
  font-size: clamp(1.75rem, 4.5vw, 2.8rem);
  font-weight: 800;
  margin-bottom: 15px;
  color: var(--text-color1);
  letter-spacing: 0.02em;
}

.hero-content > p {
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  color: var(--text-color2);
  max-width: 600px;
  margin: 0 auto 20px;
}

.last-updated {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: color-mix(in srgb, var(--border-color-hover) 12%, transparent);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  color: var(--border-color-hover);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 30%, transparent);
  margin-bottom: 0.5rem;
}

.applicability-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: color-mix(in srgb, var(--border-color-hover) 8%, transparent);
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  color: var(--text-color2);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
  margin: 15px auto;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(0.75rem, 2vw, 1rem);
  margin-top: 1.5rem;
}

.hero-btn {
  padding: clamp(0.65rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.75rem);
  border-radius: 32px;
  font-weight: 600;
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.hero-btn.primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: 1px solid var(--border-color-hover);
}

.hero-btn.secondary {
  background: transparent;
  color: var(--text-color1);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 35%, transparent);
}

.hero-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.welcome-message {
  background: var(--background-color1);
  border-radius: 12px;
  margin: clamp(1.25rem, 3vw, 1.75rem) auto;
  max-width: min(1200px, 100% - 2rem);
  padding: clamp(1.25rem, 3vw, 1.75rem);
  box-shadow: 0 8px 28px color-mix(in srgb, var(--border-color-hover) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 20%, transparent);
  border-left: 4px solid var(--border-color-hover);
}

.welcome-content h2 {
  color: var(--text-color1);
  font-size: clamp(1.25rem, 3vw, 1.8rem);
  margin: 0 0 15px 0;
}

.welcome-content > p {
  color: var(--text-color2);
  font-size: clamp(0.95rem, 2vw, 1.05rem);
  line-height: 1.6;
  margin: 0 0 20px 0;
}

.guidelines-scope {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: var(--background-color2);
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
}

.guidelines-scope i {
  color: var(--border-color-hover);
  font-size: 1.2rem;
  flex-shrink: 0;
}

.guidelines-scope span {
  color: var(--text-color2);
  font-weight: 500;
}

.legal-container {
  flex: 1 0 auto;
  width: 100%;
  max-width: min(1200px, 100% - 2rem);
  margin: 0 auto clamp(1.5rem, 4vw, 2.5rem);
  padding: 0 clamp(0.85rem, 3vw, 1.25rem);
  box-sizing: border-box;
}

.community-guidelines-content {
  background: transparent;
}

.guidelines-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 500px), 1fr));
  gap: clamp(1rem, 2.5vw, 1.5rem);
  margin-bottom: clamp(2rem, 5vw, 3rem);
}

.guideline-card {
  background: var(--background-color1);
  border-radius: 12px;
  padding: clamp(1rem, 3vw, 1.5rem);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  position: relative;
  overflow: hidden;
}

.guideline-card:hover {
  border-color: color-mix(in srgb, var(--border-color-hover) 40%, transparent);
  box-shadow: 0 8px 22px color-mix(in srgb, var(--border-color-hover) 10%, transparent);
  transform: translateY(-2px);
}

.guideline-number {
  position: absolute;
  top: 10px;
  right: 10px;
  background: color-mix(in srgb, var(--border-color-hover) 15%, var(--background-color2));
  color: var(--border-color-hover);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.guideline-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
}

.guideline-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.guideline-title i {
  color: var(--border-color-hover);
  font-size: 1.4rem;
}

.guideline-title h3 {
  color: var(--text-color1);
  font-size: clamp(1.05rem, 2.2vw, 1.3rem);
  margin: 0;
}

.guideline-subtitle {
  color: var(--text-color2);
  font-size: 0.95rem;
  font-weight: 500;
  margin: 0;
}

.allowed-section,
.prohibited-section {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid;
}

.section-header.allowed {
  border-color: #4caf50;
}

.section-header.prohibited {
  border-color: #ef5350;
}

.section-header.allowed i {
  color: #66bb6a;
}

.section-header.prohibited i {
  color: #ef5350;
}

.section-header h4 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--text-color1);
}

.allowed-list,
.prohibited-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.allowed-item,
.prohibited-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  color: var(--text-color2);
}

.allowed-item {
  background: color-mix(in srgb, #4caf50 10%, var(--background-color2));
  border: 1px solid color-mix(in srgb, #4caf50 28%, transparent);
}

.prohibited-item {
  background: color-mix(in srgb, #f44336 10%, var(--background-color2));
  border: 1px solid color-mix(in srgb, #f44336 28%, transparent);
}

.allowed-item i {
  color: #66bb6a;
  margin-top: 2px;
  flex-shrink: 0;
}

.prohibited-item i {
  color: #ef5350;
  margin-top: 2px;
  flex-shrink: 0;
}

.dual-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
  margin-bottom: 25px;
}

.role-section {
  background: var(--background-color2);
  border-radius: 10px;
  padding: 20px;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
}

.role-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.role-header i {
  color: var(--border-color-hover);
  font-size: 1.2rem;
}

.role-header h4 {
  margin: 0;
  color: var(--text-color1);
  font-size: 1.1rem;
}

.role-rules {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rule {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.rule i {
  color: var(--border-color-hover);
  margin-top: 3px;
  flex-shrink: 0;
}

.rule span {
  color: var(--text-color2);
  font-size: 0.95rem;
}

.prohibited-items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 20px;
  margin: 20px 0;
}

.prohibited-item-category {
  background: color-mix(in srgb, #f44336 10%, var(--background-color2));
  border-radius: 10px;
  padding: 15px;
  border: 1px solid color-mix(in srgb, #f44336 28%, transparent);
}

.category-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid color-mix(in srgb, #f44336 25%, transparent);
}

.category-header i {
  color: #ef5350;
}

.category-header h5 {
  margin: 0;
  color: var(--text-color1);
  font-size: 1rem;
}

.category-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.category-items span {
  color: var(--text-color2);
  font-size: 0.9rem;
  padding-left: 10px;
  position: relative;
}

.category-items span::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #ef5350;
}

.fraud-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
  gap: 15px;
  margin: 20px 0;
}

.fraud-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 15px;
  background: color-mix(in srgb, #ff9800 10%, var(--background-color2));
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, #ff9800 28%, transparent);
}

.fraud-item i {
  color: #ffb74d;
  font-size: 1.5rem;
  margin-bottom: 10px;
}

.fraud-item span {
  color: var(--text-color2);
  font-size: 0.9rem;
  font-weight: 500;
}

.safety-tips {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 0;
}

.safety-tip {
  display: flex;
  gap: 20px;
  padding: 20px;
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 25%, transparent);
}

.tip-icon {
  background: var(--border-color-hover);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tip-icon i {
  color: var(--text-color3);
  font-size: 1.2rem;
}

.tip-content h5 {
  margin: 0 0 8px 0;
  color: var(--text-color1);
  font-size: 1.05rem;
}

.tip-content p {
  margin: 0;
  color: var(--text-color2);
  font-size: 0.95rem;
  line-height: 1.5;
}

.content-rules {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
  gap: 15px;
  margin: 20px 0;
}

.content-rule {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  border-radius: 8px;
  font-weight: 500;
}

.content-rule.allowed {
  background: color-mix(in srgb, #4caf50 10%, var(--background-color2));
  color: var(--text-color2);
  border: 1px solid color-mix(in srgb, #4caf50 28%, transparent);
}

.content-rule.prohibited {
  background: color-mix(in srgb, #f44336 10%, var(--background-color2));
  color: var(--text-color2);
  border: 1px solid color-mix(in srgb, #f44336 28%, transparent);
}

.content-rule i {
  flex-shrink: 0;
}

.content-rule.allowed i {
  color: #66bb6a;
}

.content-rule.prohibited i {
  color: #ef5350;
}

.consequences-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 20px;
  margin: 20px 0;
}

.consequence-level {
  background: var(--background-color2);
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
}

.level-header {
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-color3);
}

.level-header.minor {
  background: #ff9800;
}

.level-header.moderate {
  background: #ef5350;
}

.level-header.severe {
  background: #c62828;
}

.level-header i {
  font-size: 1.2rem;
}

.level-header h5 {
  margin: 0;
  font-size: 1.1rem;
}

.level-actions {
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.level-actions span {
  color: var(--text-color2);
  font-size: 0.95rem;
  padding-left: 10px;
  position: relative;
}

.level-actions span::before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--border-color-hover);
}

.guideline-warning,
.guideline-note,
.disclaimer-notice {
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: var(--text-color2);
}

.guideline-warning {
  background: color-mix(in srgb, #ff9800 10%, var(--background-color2));
  border: 1px solid color-mix(in srgb, #ff9800 30%, transparent);
}

.guideline-warning i {
  color: #ffb74d;
  flex-shrink: 0;
}

.guideline-warning.critical {
  background: color-mix(in srgb, #f44336 10%, var(--background-color2));
  border: 1px solid color-mix(in srgb, #f44336 30%, transparent);
}

.guideline-warning.critical i {
  color: #ef5350;
}

.guideline-note {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 25%, transparent);
}

.guideline-note i {
  color: var(--border-color-hover);
  flex-shrink: 0;
}

.disclaimer-notice {
  background: color-mix(in srgb, var(--border-color-hover) 8%, var(--background-color2));
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.disclaimer-notice i {
  color: var(--border-color-hover);
  flex-shrink: 0;
}

.community-pledge {
  background: linear-gradient(
    145deg,
    var(--background-color2) 0%,
    color-mix(in srgb, var(--border-color-hover) 12%, var(--background-color1)) 100%
  );
  border-radius: 12px;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  margin: clamp(2rem, 5vw, 3rem) auto 0;
  color: var(--text-color1);
  text-align: center;
  position: relative;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 28%, transparent);
}

.community-pledge::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 200%;
  background: radial-gradient(circle, color-mix(in srgb, var(--border-color-hover) 15%, transparent) 1px, transparent 1px);
  background-size: 20px 20px;
  opacity: 0.3;
  pointer-events: none;
}

.pledge-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 25px;
  position: relative;
  z-index: 1;
}

.pledge-header i {
  font-size: 2.5rem;
  color: var(--border-color-hover);
}

.pledge-header h3 {
  font-size: clamp(1.25rem, 3vw, 1.8rem);
  margin: 0;
  color: var(--text-color1);
}

.pledge-content {
  position: relative;
  z-index: 1;
}

.pledge-content p {
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto 25px;
  color: var(--text-color2);
}

.pledge-brand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: color-mix(in srgb, var(--border-color-hover) 8%, transparent);
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.pledge-logo {
  width: clamp(72px, 18vw, 120px);
  height: auto;
  max-height: 120px;
  object-fit: contain;
  display: block;
}

.pledge-brand-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color1);
}

@media (max-width: 768px) {
  .guidelines-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .dual-column {
    grid-template-columns: 1fr;
  }

  .guideline-card {
    padding: 1.25rem;
  }

  .prohibited-items-grid,
  .fraud-grid,
  .content-rules,
  .consequences-grid {
    grid-template-columns: 1fr;
  }

  .community-pledge {
    padding: 1.5rem;
  }

  .pledge-header {
    flex-direction: column;
    text-align: center;
  }

  .safety-tip {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (hover: none) and (pointer: coarse) {
  .guideline-card:hover,
  .hero-btn:hover {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .guideline-card:hover,
  .hero-btn:hover {
    transform: none;
  }
}
</style>